import { useLiveQuery } from 'dexie-react-hooks';
import type MiniSearch from 'minisearch';
import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { db } from '../db/dexie';
import type { Attachment, GlobalTag, Item, List, ListTag } from '../db/types';
import { buildIndex, type IndexNames } from '../lib/search';

const EMPTY: never[] = [];

/**
 * Every item, live. The whole database is one user's lifetime of notes.
 *
 * Dexie hands back freshly deserialised objects on every emission, so after one
 * keystroke all several thousand rows are new objects even though exactly one
 * of them changed. Every list row downstream then counts as changed and
 * re-renders. Rows whose version is unchanged therefore keep the object they
 * already had, which is what lets the list skip them — `updated_at` moves on
 * every local write and `dirty` on every push, so between them they are a
 * complete version marker.
 */
export function useItems(): Item[] {
  const rows = useLiveQuery(() => db.items.toArray(), [], EMPTY as Item[]);
  const seen = useRef(new Map<string, Item>());

  return useMemo(() => {
    const next = new Map<string, Item>();
    const stable = rows.map((row) => {
      const previous = seen.current.get(row.id);
      const keep =
        previous && previous.updated_at === row.updated_at && previous.dirty === row.dirty
          ? previous
          : row;
      next.set(row.id, keep);
      return keep;
    });
    seen.current = next;
    return stable;
  }, [rows]);
}

export function useLists(): List[] {
  return useLiveQuery(
    async () =>
      (await db.lists.toArray())
        .filter((l) => !l.deleted)
        // Inbox always leads: it is where anything uncategorised lands, so it
        // is the one list whose position should never move.
        .sort((a, b) => Number(b.system) - Number(a.system) || a.sort_order - b.sort_order),
    [],
    EMPTY as List[],
  );
}

export function useAttachments(itemId: string | null): Attachment[] {
  return useLiveQuery(
    async () => {
      if (!itemId) return [];
      const rows = await db.attachments.where('item_id').equals(itemId).toArray();
      return rows.filter((a) => !a.deleted).sort((a, b) => a.sort_order - b.sort_order);
    },
    [itemId],
    EMPTY as Attachment[],
  );
}

/** Attachment counts for every item, so a note row can show "3 img". */
export function useAttachmentCounts(): Record<string, number> {
  const rows = useLiveQuery(() => db.attachments.toArray(), [], EMPTY as Attachment[]);
  return useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of rows) {
      if (a.deleted) continue;
      counts[a.item_id] = (counts[a.item_id] ?? 0) + 1;
    }
    return counts;
  }, [rows]);
}

/** Tags belonging to a list. Ordered, because the sidebar lets you reorder them. */
export function useListTags(): ListTag[] {
  return useLiveQuery(
    async () =>
      (await db.listTags.toArray())
        .filter((t) => !t.deleted)
        .sort((a, b) => a.sort_order - b.sort_order),
    [],
    EMPTY as ListTag[],
  );
}

export function useGlobalTags(): GlobalTag[] {
  return useLiveQuery(
    async () =>
      (await db.globalTags.toArray())
        .filter((t) => !t.deleted)
        .sort((a, b) => a.sort_order - b.sort_order),
    [],
    EMPTY as GlobalTag[],
  );
}

/** Everything the views and the sidebar need to turn ids back into names. */
export function useCatalog() {
  const lists = useLists();
  const listTags = useListTags();
  const globalTags = useGlobalTags();
  return useMemo(() => ({ lists, listTags, globalTags }), [lists, listTags, globalTags]);
}

/**
 * The search index, built only when something actually reads it.
 *
 * Building it is a full pass over every note in the database, and the command
 * palette is the only thing that ever looks at it. Building it eagerly meant
 * paying that pass on *every keystroke* — each character typed writes the note,
 * which re-runs the live query, which handed a new item array to a `useMemo`
 * that promptly re-indexed the entire collection. At a few hundred notes that
 * is a stutter; at a few thousand it is more than a tenth of a second of
 * blocked main thread per character, and typing runs away from the UI.
 *
 * So the caller gets a getter instead of an index. Nothing is built until the
 * palette asks, and what is built is kept until the notes actually change.
 */
export function useSearchIndex(items: Item[]) {
  const live = useMemo(() => items.filter((i) => !i.deleted), [items]);
  const { lists, listTags, globalTags } = useCatalog();

  const names = useMemo(
    () => ({
      lists: new Map(lists.map((l) => [l.id, l.name])),
      tags: new Map([...listTags, ...globalTags].map((t) => [t.id, t.name])),
    }),
    [lists, listTags, globalTags],
  );

  const cache = useRef<{ live: Item[]; names: IndexNames; index: MiniSearch } | null>(null);

  return useCallback(() => {
    const hit = cache.current;
    if (!hit || hit.live !== live || hit.names !== names) {
      cache.current = { live, names, index: buildIndex(live, names) };
    }
    return cache.current!.index;
  }, [live, names]);
}

export function useConflicts() {
  return useLiveQuery(() => db.conflicts.reverse().toArray(), [], EMPTY as never[]);
}

/**
 * Which shell to mount. Desktop is the workspace; mobile does deliberately less.
 *
 * Subscribes to `resize` as well as the media query: some WebViews — including
 * the one Capacitor ships — do not reliably fire `change` on a MediaQueryList
 * when the viewport is resized programmatically or the device rotates.
 */
export function useIsMobile(breakpoint = 900) {
  const query = `(max-width: ${breakpoint}px)`;

  const subscribe = useCallback(
    (notify: () => void) => {
      const mq = window.matchMedia(query);
      mq.addEventListener('change', notify);
      window.addEventListener('resize', notify);
      window.addEventListener('orientationchange', notify);
      return () => {
        mq.removeEventListener('change', notify);
        window.removeEventListener('resize', notify);
        window.removeEventListener('orientationchange', notify);
      };
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
